import { useState, useEffect, useCallback } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import { SupplierProfile, Database } from '@mondivago/types';
import type { SupplierProfileResult } from '@mondivago/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sw = () => (supabase as any).from('supplier_workers');

type ProfileRow = Database['public']['Tables']['suppliers']['Row'] & {
  content_cities: {
    name: string;
    content_countries: {
      name: string;
      iso2: string;
    } | null;
  } | null;
};

function normalizeProfile(data: ProfileRow): SupplierProfile {
  return {
    ...data,
    city: data.content_cities?.name ?? null,
    country: data.content_cities?.content_countries?.name ?? null,
    country_iso2: data.content_cities?.content_countries?.iso2 ?? null,
    parent_details: null,
  } as SupplierProfile;
}

/**
 * Unified supplier-profile hook.
 *
 * Returns **both** the current user's own record (`profile`) and their
 * parent supplier record (`agency`) when applicable, so consumers no longer
 * need to chain two separate hooks.
 */
export function useSupplierProfile(): SupplierProfileResult {
  const { user } = useAuth();
  const [profile, setProfile] = useState<SupplierProfile | null>(null);
  const [agency, setAgency] = useState<SupplierProfile | null>(null);
  const [role, setRole] = useState<'individual' | 'agency' | null>(null);
  const [workerRole, setWorkerRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id;

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setProfile(null);
      setAgency(null);
      setRole(null);
      setWorkerRole(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch worker record first to see if user is a supplier worker (invited team member or owner worker)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: agentData, error: agentError } = await (sw as any)()
        .select('id, user_id, supplier_id, first_name, last_name, email, phone, role, is_active')
        .eq('user_id', userId)
        .maybeSingle();

      if (agentError) throw agentError;

      // 2. Fetch the current user's direct supplier record (if any)
      const { data: directData, error: dbError } = await supabase
        .from('suppliers')
        .select(
          'id, short_id, user_id, company_name, contact_email, contact_phone, photo_url, city_id, country_id, content_cities(name, content_countries(name, iso2)), languages, specializations, years_of_experience, status, profile_visible, onboarding_completed, social_links, type, created_at, bank_iban, bank_account_holder_name, bank_swift_bic, bank_name, bank_country_iso2, vat_number, billing_address, updated_at, description'
        )
        .eq('user_id', userId)
        .maybeSingle();

      if (dbError) throw dbError;

      if (agentData) {
        // If the user has a direct supplier record and its id matches agentData.supplier_id, they are the owner
        if (directData && (directData as Record<string, unknown>).id === agentData.supplier_id) {
          const typedData = directData as unknown as ProfileRow;
          const normalized = normalizeProfile(typedData);

          setProfile(normalized);
          setRole((normalized.type ?? 'individual') as 'individual' | 'agency');
          setAgency(null);
          setWorkerRole(agentData.role);
          setLoading(false);
          return;
        }

        // Otherwise, user is an invited worker belonging to agency `agentData.supplier_id`
        const { data: agencyData, error: agencyError } = await supabase
          .from('suppliers')
          .select(
            'id, short_id, user_id, company_name, contact_email, contact_phone, photo_url, city_id, country_id, content_cities(name, content_countries(name, iso2)), languages, specializations, years_of_experience, status, profile_visible, onboarding_completed, social_links, type, created_at, bank_iban, bank_account_holder_name, bank_swift_bic, bank_name, bank_country_iso2, vat_number, billing_address, updated_at, description'
          )
          .eq('id', agentData.supplier_id)
          .maybeSingle();

        if (agencyError) throw agencyError;

        if (agencyData) {
          const normalized = normalizeProfile(agencyData as unknown as ProfileRow);
          setAgency(normalized);
        }
        setProfile(null);
        setRole('individual');
        setWorkerRole(agentData.role);
        setLoading(false);
        return;
      }

      if (directData) {
        const typedData = directData as unknown as ProfileRow;
        const normalized = normalizeProfile(typedData);

        setProfile(normalized);
        setRole((normalized.type ?? 'individual') as 'individual' | 'agency');
        setAgency(null);
        setWorkerRole(null);
        return;
      }

      setProfile(null);
      setAgency(null);
      setRole(null);
      setWorkerRole(null);
    } catch {
      setError('Error loading profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateProfile = useCallback(
    async (updates: Partial<SupplierProfile>) => {
      if (!userId) return { success: false, error: 'Not authenticated' };

      try {
        const { data, error: dbError } = await supabase
          .from('suppliers')
          .update(updates)
          .eq('user_id', userId)
          .select(
            'id, short_id, user_id, company_name, contact_email, contact_phone, photo_url, city_id, country_id, content_cities(name, content_countries(name, iso2)), languages, specializations, years_of_experience, status, profile_visible, onboarding_completed, social_links, type, created_at, bank_iban, bank_account_holder_name, bank_swift_bic, bank_name, bank_country_iso2, vat_number, billing_address, updated_at, description'
          )
          .maybeSingle();

        if (dbError) throw dbError;

        if (data) {
          setProfile(normalizeProfile(data as unknown as ProfileRow));
        }
        return { success: true };
      } catch {
        return { success: false, error: 'Error updating profile. Please try again.' };
      }
    },
    [userId]
  );

  const upsertProfile = useCallback(
    async (profileData: Record<string, unknown>) => {
      if (!userId) return { success: false, error: 'Not authenticated' };

      try {
        const SELECT_COLS =
          'id, short_id, user_id, company_name, contact_email, contact_phone, photo_url, city_id, country_id, content_cities(name, content_countries(name, iso2)), languages, specializations, years_of_experience, status, profile_visible, onboarding_completed, social_links, type, created_at, bank_iban, bank_account_holder_name, bank_swift_bic, bank_name, bank_country_iso2, vat_number, billing_address, updated_at, description';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error: dbError } = await (supabase.from('suppliers') as any)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .upsert({ ...profileData, user_id: userId } as any, { onConflict: 'user_id' })
          .select(SELECT_COLS)
          .single();

        if (dbError) throw dbError;

        let finalData: SupplierProfile | null = null;
        if (data) {
          finalData = normalizeProfile(data as unknown as ProfileRow);
          setProfile(finalData);
        }

        return { success: true, data: finalData };
      } catch {
        return { success: false, error: 'Error saving profile. Please try again.' };
      }
    },
    [userId]
  );

  const getActiveBookingsCount = useCallback(async () => {
    if (!userId) return 0;
    try {
      const { count } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('supplier_id', userId)
        .in('status', ['confirmed', 'pending']);
      return count ?? 0;
    } catch {
      return 0;
    }
  }, [userId]);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  const supplierType = (profile?.type as 'individual' | 'agency') ?? null;
  const isAgencyAdmin = supplierType === 'agency' || ['admin', 'owner', 'agency'].includes(workerRole ?? '');

  return {
    profile,
    agency,
    type: supplierType,
    role,
    isAgencyAdmin,
    workerRole,
    loading,
    error,
    refresh: fetchProfile,
    updateProfile,
    upsertProfile,
    getActiveBookingsCount,
  };
}
